<template>
    <sl-card :class="{ 'sl-theme-dark': isDarkMode }">
        <template v-if="!isFetching">
            <h3 slot="header">
                <i v-if="icon" :class="icon" />
                {{ entity?.name }}
            </h3>
            <img
                slot="image"
                id="thumbnail"
                v-if="entity?.thumbnail"
                :src="entity?.thumbnail"
                alt="thumbnail"
                :style="imageStyle"
            />
            <p v-if="entity?.short_description" class="body">
                {{ entity?.short_description }}
            </p>
            <div slot="footer" v-if="tags?.length || showPath">
                <!-- Tags -->
                <div class="entity-tags" v-if="tags?.length">
                    <Tag
                        v-for="tag in tags"
                        class="tag"
                        :severity="tag.severity"
                    >
                        {{ tag.label }}
                    </Tag>
                </div>
                <!-- Path -->
                <div class="path" v-if="showPath">
                    <span v-if="domainName" class="segment">
                        <i class="pi pi-cloud"></i>
                        <span>{{ domainName }}</span>
                    </span>
                    <span v-if="roleName" class="segment">
                        <i class="pi pi-chevron-right"></i>
                        <i class="pi pi-id-card"></i>
                        <span>{{ roleName }}</span>
                    </span>
                    <span v-if="businessEntityName" class="segment">
                        <i class="pi pi-chevron-right"></i>
                        <i class="pi pi-building"></i>
                        <span>{{ businessEntityName }}</span>
                    </span>
                    <span v-if="productName" class="segment">
                        <i class="pi pi-chevron-right"></i>
                        <i class="pi pi-box"></i>
                        <span>{{ productName }}</span>
                    </span>
                    <span v-if="processName" class="segment">
                        <i class="pi pi-chevron-right"></i>
                        <i class="pi pi-share-alt"></i>
                        <span>{{ processName }}</span>
                    </span>
                    <span v-if="taskName" class="segment">
                        <i class="pi pi-chevron-right"></i>
                        <i class="pi pi-check-square"></i>
                        <span>{{ taskName }}</span>
                    </span>
                </div>
            </div>
        </template>
        <template v-else>
            <sl-skeleton slot="header" effect="sheen"></sl-skeleton>
            <p class="body">
                <sl-skeleton effect="sheen"></sl-skeleton>
                <sl-skeleton effect="sheen"></sl-skeleton>
            </p>
        </template>
    </sl-card>
</template>

<script setup lang="ts">
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/skeleton/skeleton.js";
import "@shoelace-style/shoelace/dist/themes/dark.css";
import "@shoelace-style/shoelace/dist/themes/light.css";

import Tag from "primevue/tag";
import { computed } from "vue";

import { useUIManager } from "@/stores/ui-store";

const props = withDefaults(
    defineProps<{
        entity: {
            identity: string;
            name: string;
            short_description?: string | null;
            thumbnail?: string | null;
        };
        tags: {
            label: string;
            severity: "primary" | "secondary" | "info";
        }[];
        icon?: string;
        domainName?: string;
        productName?: string;
        processName?: string;
        taskName?: string;
        roleName?: string;
        businessEntityName?: string;
        entityType: string;
        borderColor?: string;
        showPath?: boolean;
        isFetching?: boolean;
    }>(),
    {
        showPath: true,
        borderColor: "transparent",
    }
);

const ui = useUIManager();
const isDarkMode = computed(() => ui.darkMode.value);

const imageStyle = computed(() => {
    // Invert colors for BPMN diagrams in dark mode
    if (isDarkMode.value && props.entityType.toLowerCase() === "process") {
        return { filter: "invert(1) hue-rotate(180deg)" };
    }
    return {};
});
</script>

<style scoped lang="scss">
sl-card {
    --border-width: 0;
    --border-radius: 0;

    width: 32em;
    max-width: 400px;
    transition: all 250ms ease;

    border-bottom: var(--stekz-border-width) solid v-bind("props.borderColor");

    &::part(base) {
        transition: all 250ms ease;
    }
    &::part(body) {
        flex-grow: 1;
    }

    h3 {
        margin-bottom: 0.375em;
        margin-top: 0.375em;

        > i {
            margin-right: 0.5em;
        }
    }
}

.path {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.85rem;
    flex-wrap: wrap;

    .pi-chevron-right {
        font-size: 0.5rem;
    }
}

.segment {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    flex-wrap: nowrap;
    white-space: nowrap;
}

.entity-tags {
    margin-bottom: 1em;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.body {
    max-height: 500px;
    overflow-y: scroll;
    word-break: break-all;
}

sl-skeleton {
    width: 300px;
}

p sl-skeleton {
    &:not(:last-of-type) {
        margin-bottom: 1rem;
    }

    &:last-of-type {
        width: 250px;
    }
}

#thumbnail {
    max-height: 200px;
    object-fit: contain;
}
</style>
